<template>
    <div v-if="show && loaded" class="nevs-popup-container">
        <div class="nevs-popup overview-persons-popup">
            <div class="overview-persons-title">{{ title }}</div>
            <div class="overview-persons-list">
                <NevsSimpleGrid :fields="fields" :records="gridRecords" :clickable="false"
                                :empty-text="$LANG.Get('statistics.noRecords')"></NevsSimpleGrid>
            </div>
            <div class="nevs-popup-buttons">
                <NevsButton class="primary" @click="closeClick()">
                    <i class="fa-solid fa-xmark"></i>
                    {{ $LANG.Get('buttons.close') }}
                </NevsButton>
            </div>
        </div>
    </div>
</template>

<script>
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsSimpleGrid from "@/components/nevs/NevsSimpleGrid.vue";

export default {
    name: "ModalOverviewPersons",
    components: {NevsSimpleGrid, NevsButton},
    props: {
        show: {
            type: Boolean,
            default: false
        },
        // Which of the four overview figures to list: 'active', 'lapsed', 'absent' or 'expiring'.
        type: {
            type: String,
            default: ''
        },
        // The tile's own label, so the list is headed by exactly the wording that was clicked.
        title: {
            type: String,
            default: ''
        }
    },
    emits: ['close'],
    data() {
        return {
            loaded: false,
            records: []
        }
    },
    computed: {
        // Every list is "who, on what membership, and the one date that matters" - only the meaning of that
        // date changes with the figure, so the last column is titled per type rather than laid out per type.
        detailLabel() {
            switch (this.type) {
                case 'lapsed':
                    return this.$LANG.Get('statistics.expiredOn');
                case 'absent':
                    return this.$LANG.Get('statistics.lastVisit');
                case 'expiring':
                    return this.$LANG.Get('statistics.expiresOn');
                default:
                    return this.$LANG.Get('statistics.validUntil');
            }
        },
        fields() {
            return [
                {name: 'person', label: this.$LANG.Get('statistics.person'), align: 'L'},
                {name: 'membership', label: this.$LANG.Get('statistics.membership'), align: 'L'},
                {name: 'detail', label: this.detailLabel, align: 'L'}
            ];
        },
        gridRecords() {
            return this.records.map((record) => ({
                person: record.person,
                membership: record.membership,
                detail: this.type === 'absent'
                    ? (record.last_visit_display !== '' ? record.last_visit_display : this.$LANG.Get('statistics.neverVisited'))
                    : record.end_date_display
            }));
        }
    },
    watch: {
        show() {
            this.loaded = false;
            this.records = [];
            if (!this.show) {
                return;
            }
            let vm = this;
            this.$API.APICall('get', 'overview-statistics/general-records', {type: this.type}, (data, success) => {
                if (success) {
                    vm.records = data.records;
                    vm.loaded = true;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                    vm.$emit('close');
                }
            });
        }
    },
    methods: {
        closeClick() {
            this.$emit('close');
        }
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

/* Wider than the default popup: three columns of names and dates need the room. */
.overview-persons-popup {
    width: 700px;
    max-width: calc(100vw - 40px);
    display: flex;
    flex-direction: column;
    /* The list below scrolls on its own, so the popup itself must not: that keeps the title and the close
       button in place however many records there are. The height cap comes from .nevs-popup. */
    overflow: hidden;
}

.overview-persons-title {
    flex: 0 0 auto;
    padding: 20px 20px 10px;
    font-weight: bold;
    font-size: 20px;
}

.overview-persons-list {
    /* min-height lets this shrink below the table's height, which is what makes it - rather than the popup -
       the box that scrolls. */
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 0 20px;
}

/* The column headings stay put while the list scrolls under them. The separating line is drawn as an inset
   shadow because a border on a sticky cell is dropped by the table's collapsed borders. */
.overview-persons-list :deep(th) {
    position: sticky;
    top: 0;
    background: $popup-background;
    box-shadow: inset 0 -1px 0 #eee;
}

.nevs-popup-buttons {
    flex: 0 0 auto;
}
</style>
